import { useCallback, useEffect, useState } from 'react';
import { StarGridView } from '../starGridConstants';

export interface UseStarGridNavResult {
  activeView: StarGridView;
  /** For the `report` view, the selected PnL report id (else undefined). */
  activeReportId: number | undefined;
  setActiveView: (view: StarGridView) => void;
  /** Navigate to a specific created report's detail view (`#report-<id>`). */
  openReport: (reportId: number) => void;
  navigationOpen: boolean;
  setNavigationOpen: (open: boolean) => void;
}

/** Fixed (non-parametric) views addressable by a bare hash. */
const KNOWN_VIEWS: readonly StarGridView[] = ['home', 'infra', 'create-report', 'data-sync'];
const DEFAULT_VIEW: StarGridView = 'home';

interface Resolved {
  view: StarGridView;
  reportId: number | undefined;
}

/** Parse `window.location.hash` into a view (+ optional report id). `#report-42` → report detail. */
function resolveFromHash(): Resolved {
  const raw = window.location.hash.replace(/^#/, '');
  const reportMatch = /^report-(\d+)$/.exec(raw);
  if (reportMatch) {
    return { view: 'report', reportId: Number(reportMatch[1]) };
  }
  if ((KNOWN_VIEWS as readonly string[]).includes(raw)) {
    return { view: raw as StarGridView, reportId: undefined };
  }
  return { view: DEFAULT_VIEW, reportId: undefined };
}

/**
 * Shell state for the StarGrid AppLayout: which sub-view is active (+ selected report id) and
 * nav open/closed. The active view is mirrored to the URL hash (`/star-grid#reports`,
 * `/star-grid#report-42`) so it is bookmarkable, survives reloads, and stays in sync with
 * browser back/forward navigation.
 */
export function useStarGridNav(): UseStarGridNavResult {
  const [{ view: activeView, reportId: activeReportId }, setResolved] = useState<Resolved>(resolveFromHash);
  const [navigationOpen, setNavigationOpen] = useState<boolean>(true);

  useEffect(() => {
    // Normalize the hash to the resolved view on first mount (e.g. no/unknown hash → #home)...
    const resolved = resolveFromHash();
    const expectedHash = resolved.view === 'report' ? `report-${resolved.reportId}` : resolved.view;
    if (window.location.hash.replace(/^#/, '') !== expectedHash) {
      window.location.hash = expectedHash;
    }
    // ...and keep state in sync when the user uses browser back/forward or edits the hash.
    const onHashChange = () => setResolved(resolveFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const setActiveView = useCallback((view: StarGridView) => {
    setResolved({ view, reportId: undefined });
    if (window.location.hash.replace(/^#/, '') !== view) {
      window.location.hash = view;
    }
  }, []);

  const openReport = useCallback((reportId: number) => {
    setResolved({ view: 'report', reportId });
    const hash = `report-${reportId}`;
    if (window.location.hash.replace(/^#/, '') !== hash) {
      window.location.hash = hash;
    }
  }, []);

  return { activeView, activeReportId, setActiveView, openReport, navigationOpen, setNavigationOpen };
}
